import { type ReactNode } from 'react';

import { type HelpTooltipProps } from '../components/Tooltips/Help.js';

export enum EntityType {
  Encounter = 'encounter',
  EhrInformation = 'ehrInformation',
  PatientOrder = 'patientOrder',
  ExternalDocumentPacket = 'externalDocumentPacket',
}

export function isTruthy<T>(value: T | null | undefined | false): value is T {
  return !!value;
}

export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function assertNever(message: string, obj: never): never {
  void obj; // to avoid eslint errors
  throw new Error(message);
}

export function assert(condition: unknown, message?: string): asserts condition {
  if (!condition) {
    throw new Error(message || 'Assertion failure');
  }
}

export function isEnumMember<T extends object>(enumObj: T, value: unknown): value is T[keyof T] {
  return Object.values(enumObj).includes(value);
}

export function parseValue<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function parseJsonSafely(jsonString: string): {
  parsedJson: Record<string, any> | null;
  error: string | null;
} {
  try {
    const parsed = JSON.parse(jsonString);
    return { parsedJson: parsed, error: null };
  } catch (err) {
    const errorMessage =
      err instanceof SyntaxError ? `Invalid JSON: ${err.message}` : 'Invalid JSON format';
    return { parsedJson: null, error: errorMessage };
  }
}

export function isObjKey<T extends object>(key: PropertyKey, obj: T): key is keyof T {
  return key in obj;
}
export function hasValidMultiEnumWithOther<T>(props: {
  items: Record<string, any>[];
  valueKey: string;
  rawValueKey: string;
  otherValue: T;
}): boolean {
  const { items, valueKey, rawValueKey, otherValue } = props;
  return items.every(
    (item) =>
      isTruthy(item[valueKey]) && (item[valueKey] !== otherValue || isTruthy(item[rawValueKey]))
  );
}

export type RequireLabelOrAria =
  | { label: ReactNode; 'aria-label': string }
  | { label: string; 'aria-label'?: never }
  | { label?: never; 'aria-label': string };

export type RequireLabelWithOptionalHelpTooltipOrAria =
  | ({ label: ReactNode; 'aria-label'?: string } & { helpTooltipProps?: HelpTooltipProps })
  | ({ label: string; 'aria-label': never } & { helpTooltipProps?: HelpTooltipProps })
  | ({ label?: never; 'aria-label': string } & { helpTooltipProps?: never });
